/**
 * The single data hook ALL tile views use (S3). TanStack Query (G15: one
 * shared client) keyed `['dashboard-tile', dashboardId, tileId,
 * runInputsHash, filtersHash]` — the tile's data inputs and parameters
 * share one hash; cross-filters (S11) invalidate exactly like parameters.
 *
 * UI Quality Contract §3 is implemented HERE, structurally:
 *  - `placeholderData: keepPreviousData` + throw-on-failure keep the last
 *    good data rendered — a refetch (or its failure) never blanks a tile;
 *  - `isFirstLoad` (skeleton) is distinct from `isRefetching` (chrome
 *    indicator only) — every later tile view inherits flicker-free refresh.
 */
import { useEffect, useMemo, useRef } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import type { DashboardParameter } from '../types/dashboardTypes';
import type { DashboardTileRuntime, TileRunResult } from '../types/dashboardRuntime';
import { relevantFiltersForTile } from '../services/crossFilter';
import { dashboardTileQueryKey, selectDashboardTileQuery, stableValueKey } from '../services/dashboardTileCache';
import { tileRunInputsHash } from '../services/paramHashing';
import { startTileExecution, type QueryBackedTile } from '../services/tileExecutionService';
import { useDashboardStore } from '../store/dashboardStore';
import {
  selectActiveFilters,
  useDashboardCrossFilterStore,
} from '../store/dashboardCrossFilterStore';
import {
  selectParamValues,
  selectParamsHydrated,
  useDashboardParamStore,
} from '../store/dashboardParamStore';
import { selectTileRuntime, useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';

const NO_PARAMS: DashboardParameter[] = [];

export type UseTileResultValue = {
  /** Last successful run (kept during refetches and across failures). */
  result: (TileRunResult & { ok: true }) | undefined;
  runtime: DashboardTileRuntime | undefined;
  /** No data yet — render the skeleton. */
  isFirstLoad: boolean;
  /** Refreshing behind existing data — chrome indicator only, never blank. */
  isRefetching: boolean;
  error: string | undefined;
};

export function useTileResult(dashboardId: string, tile: QueryBackedTile): UseTileResultValue {
  const queryClient = useQueryClient();
  const runtime = useDashboardRuntimeStore(selectTileRuntime(dashboardId, tile.id));
  const parameters =
    useDashboardStore((s) => s.dashboards.find((d) => d.id === dashboardId)?.parameters) ?? NO_PARAMS;
  const values = useDashboardParamStore(selectParamValues(dashboardId));
  const paramsHydrated = useDashboardParamStore(selectParamsHydrated(dashboardId));
  const engine = getEngineForConnection(tile.connectionId);
  // Hash the RAW inputs, never resolved instants — see paramHashing docblock.
  // The tile's own data inputs are in the hash: a definition edit from any
  // writer (dialog, MCP patch, import) re-keys and fetches, no refetch call
  // needed. Listed field-by-field so a layout drag (new tile object, same
  // inputs) doesn't recompute.
  const runInputsHash = useMemo(
    () => tileRunInputsHash({ tile, engine, parameters, values }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the data inputs only
    [tile.connectionId, tile.database, tile.source.sql, tile.resultSetIndex, engine, parameters, values],
  );
  // Only the filters THIS tile participates in enter the key — an unbound
  // sibling's key is untouched by other tiles' cross-filters (no refetch).
  const activeFilters = useDashboardCrossFilterStore(selectActiveFilters(dashboardId));
  const filters = useMemo(
    () => relevantFiltersForTile(tile, activeFilters, engine),
    [tile, activeFilters, engine],
  );
  const filtersHash = useMemo(() => stableValueKey(filters), [filters]);
  const store = useDashboardRuntimeStore.getState();
  const queryKey = useMemo(
    () => dashboardTileQueryKey(dashboardId, tile.id, runInputsHash, filtersHash),
    [dashboardId, tile.id, runInputsHash, filtersHash],
  );

  // Cancel handle of the run currently in flight for THIS tile. Wired so a
  // superseding run (params/filters changed), a same-key scheduler refetch, or
  // the tile's removal stops the adapter query server-side instead of letting
  // it run to completion unobserved (D3). `settle` is executionId-guarded, so a
  // cancelled run's late resolution can never clobber the newer run's state.
  const inFlightCancel = useRef<(() => void) | null>(null);
  useEffect(() => () => inFlightCancel.current?.(), []);

  const query = useQuery({
    queryKey,
    // Gate on hydration: restored selections must not race a defaults run.
    enabled: Boolean(tile.connectionId && tile.source.sql.trim() && paramsHydrated),
    // The scheduler owns cadence; failed runs must not auto-retry (a
    // policy-blocked tile would hammer the policy) and results never go
    // stale on their own. gc is manual: evictAllDashboardTileQueries on tab
    // close; retained across in-tab dashboard switches (S13/S15 need it).
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<TileRunResult & { ok: true }> => {
      // A new run supersedes any run still in flight for this tile.
      inFlightCancel.current?.();
      // Automatic fetches (mount, scheduler refetch, invalidation) run under
      // 'interval' policy: unattended, so never write without main-granted
      // trust. Explicit user runs (S6 buttons) will pass 'manual'.
      const { executionId, promise, cancel } = startTileExecution(
        tile,
        { parameters, values, engine },
        filters,
        { trigger: 'interval', dashboardId },
      );
      inFlightCancel.current = cancel;
      // React Query aborts `signal` on a same-key supersession or an explicit
      // cancelQueries — propagate that to the adapter query too.
      signal.addEventListener('abort', cancel, { once: true });
      store.begin(dashboardId, tile.id, executionId);
      try {
        const result = await promise;
        const applied = store.settle(dashboardId, tile.id, executionId, result);
        if (!applied) {
          // Superseded: a newer run of this tile (a manual "Refresh now"
          // lands through `runTileManually`, outside this queryFn) began
          // after this one, and ITS outcome is the tile's truth. Hand the
          // cache's current entry back unchanged so this late result never
          // overwrites fresher data; with nothing cached yet, the newer
          // run's own write lands on top of whatever is returned here.
          const current = queryClient.getQueryData<TileRunResult & { ok: true }>(queryKey);
          if (current) return current;
        }
        // Throwing keeps the previous data in the query cache (never blank);
        // badges read the failure details from the runtime store.
        if (!result.ok) throw new Error(result.error);
        return result;
      } finally {
        signal.removeEventListener('abort', cancel);
        if (inFlightCancel.current === cancel) inFlightCancel.current = null;
      }
    },
  });

  useEffect(() => {
    selectDashboardTileQuery(queryClient, queryKey, Boolean(tile.connectionId && tile.source.sql.trim() && paramsHydrated));
  }, [queryClient, queryKey, paramsHydrated, tile.connectionId, tile.source.sql]);

  return {
    result: query.data ?? runtime?.lastSuccess?.result,
    runtime,
    isFirstLoad: query.isLoading,
    isRefetching: query.isFetching && !query.isLoading,
    error: query.error instanceof Error ? query.error.message : undefined,
  };
}
